/**
 * /car-paint-code — the page. Logic lives in engine.js (tested in node);
 * this file draws and wires. Nothing here scrolls the page on its own, and
 * nothing links out: sources and the makers' VIN pages are named as text.
 */
import { F, fold, codeKey, indexCatalog, classify, lookupCode, readLabel, brandFromManufacturer, filterColors, familiesOf,
    fitsBrand, modelIndex, resolveModel, modelLabel, inModel, sourceInfo, plainAddress } from './engine.js';
import { decode as decodeVin } from '../vin/vin.js';
import { mountCalc, presetCalc } from './calc-ui.js';

const CFG = window.__CARPAINT_CFG || { lang: {} };
const SLUG = 'car-paint-code';
const $ = (id) => document.getElementById(id);
const t = (k, vars) => {
    let s = (CFG.lang && CFG.lang[k]) || k;
    if (vars) for (const v in vars) s = s.split(':' + v).join(vars[v]);
    return s;
};
const ae = (ev, d) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (e) {} };
const stage = (s, d) => { try { window.__stage && window.__stage(s, d); } catch (e) {} };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
/* Russian names and the Russian market first — for the Russian page only.
   Cyrillic label reading (UAZ, LADA, Ukrainian cars) — for ru and uk. */
const RU = CFG.locale === 'ru';
const CYR_OCR = CFG.locale === 'ru' || CFG.locale === 'uk';

/* Representative display colours per family — used only where the maker
   published no display colour, and then marked as approximate on screen. */
const FAMILY_HEX = {
    white: '#F2F2EF', black: '#1C1C1E', silver: '#B9BCBF', gray: '#7B7F84', red: '#B3262E', blue: '#2F5DA8',
    green: '#3F7A4B', yellow: '#E7C11F', orange: '#E07A2A', brown: '#6E4B34', beige: '#D8C7A6', gold: '#B8914A',
    purple: '#6A4A8C', other: '#9AA0A6',
};

let IDX = null;          // engine index
let CUR = null;          // what the result area shows: { brand, code, model, q, fam }
let ocrEngine = null;
let completed = false;   // tool_complete goes out once per visit, at the first real answer

/* The first time the page ANSWERS something — a code resolved, a label
   read, a colour opened — is this tool's completion. Browsing makes is not. */
function complete(kind, d) {
    if (completed) return;
    completed = true;
    try { window.__aeToolComplete && window.__aeToolComplete(Object.assign({ kind }, d || {})); } catch (e) {}
}

/* ── data ─────────────────────────────────────────────────────────────────── */

/* The makers' catalogue is a static module; owners' codes come from the API.
   The make list must not wait for the API (measured: a busy PHP pool held it
   4.5 s) — the catalogue renders at once, owners' codes join when they come. */
async function loadCatalog() {
    const community = fetch(CFG.communityUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
    const base = (await import('./catalog.js')).default;
    // deep-ish copy: the index adds owners' rows to brand colour lists
    const build = (c) => indexCatalog({ ...base, brands: base.brands.map((b) => ({ ...b, colors: b.colors.map((r) => r.slice()) })) },
        (c && c.items) || [], (c && c.labels) || []);
    const quick = await Promise.race([community, new Promise((res) => setTimeout(() => res(null), 1500))]);
    IDX = build(quick);
    if (!quick) community.then((c) => { if (c && ((c.items && c.items.length) || (c.labels && c.labels.length))) IDX = build(c); });
    return IDX;
}

/* ── brand picker ─────────────────────────────────────────────────────────── */

function brandLabel(b) { return RU && b.nameRu ? b.name + ' · ' + b.nameRu : b.name; }

function renderBrandPicker() {
    const grid = $('cpBrandGrid');
    grid.innerHTML = '';
    for (const b of IDX.brands.slice(0, 18)) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cp-chip';
        btn.dataset.brand = b.id;
        btn.textContent = brandLabel(b);
        btn.addEventListener('click', () => openBrand(b.id, { via: 'chip', push: true }));
        grid.appendChild(btn);
    }
    const sel = $('cpBrandAll');
    const rest = IDX.brands.slice().sort((a, c) => a.name.localeCompare(c.name));
    for (const b of rest) {
        const o = document.createElement('option');
        o.value = b.id; o.textContent = brandLabel(b);
        sel.appendChild(o);
    }
    $('cpBrandAllWrap').hidden = false;
    sel.addEventListener('change', () => { if (sel.value) openBrand(sel.value, { via: 'select', push: true }); });
    const dl = $('cpAskBrands');
    for (const b of rest) { const o = document.createElement('option'); o.value = b.name; dl.appendChild(o); }
}

function markPicker(id) {
    for (const c of $('cpBrandGrid').querySelectorAll('.cp-chip')) c.setAttribute('aria-pressed', c.dataset.brand === id ? 'true' : 'false');
    const sel = $('cpBrandAll'); if (sel) sel.value = id || '';
}

/* ── recent lookups ───────────────────────────────────────────────────────── */

/* A driver comes back for the same car — at the shop, at the colourist's.
   The last few answers stay on this device only (localStorage). */
const RECENT = 'recent';
function recentList() {
    try {
        const a = window.__toolSettings ? window.__toolSettings.load(SLUG, RECENT, []) : [];
        return (Array.isArray(a) ? a : []).filter((x) => x && x.b && IDX && IDX.byId.has(x.b)).slice(0, 5);
    } catch (e) { return []; }
}
function remember(entry) {
    try {
        const same = (x) => x.b === entry.b && (x.m || '') === (entry.m || '') && (x.c || '') === (entry.c || '');
        const a = [entry].concat(recentList().filter((x) => !same(x))).slice(0, 5);
        window.__toolSettings && window.__toolSettings.save(SLUG, RECENT, a);
    } catch (e) {}
    renderRecent();
}
function recentLabel(x) {
    const b = IDX.byId.get(x.b);
    return [b ? b.name : x.b, x.m ? modelLabel(b, x.m) : '', x.c || ''].filter(Boolean).join(' ');
}
function renderRecent() {
    const box = $('cpRecent');
    if (!box || !IDX) return;
    const list = recentList();
    box.hidden = !list.length;
    box.querySelector('[data-recent-list]').innerHTML = list.map((x, i) =>
        `<button type="button" class="cp-chip" data-recent="${i}">${esc(recentLabel(x))}</button>`).join('');
}

/* ── the "where is the label" diagram ─────────────────────────────────────── */

/* A car from above, front at the top, left-hand drive (the caption says so):
   left and right are what a side view cannot show — the driver's pillar and
   the passenger's used to sit on one spot, their numbers on top of each other.
   Every spot is at least 22 units from every other, the dots are 20 across. */
const DOTS = {
    under_hood_radiator_support: [100, 28], hood_underside: [100, 58], under_hood_strut_tower: [62, 74],
    engine_bay_vin_plate: [138, 74], under_hood_firewall: [100, 90], glovebox: [130, 130],
    driver_door_jamb: [42, 166], driver_b_pillar: [42, 193], rear_door_opening: [42, 222],
    passenger_door_jamb: [158, 166], passenger_b_pillar: [158, 193], fuel_filler_door: [160, 254],
    trunk_side_panel: [132, 268], trunk_floor_spare_wheel: [96, 262], trunk_lid: [100, 286],
};

function carSvg(spots) {
    const dots = [];
    spots.forEach((s, i) => {
        const p = DOTS[s.where];
        if (!p) return;
        dots.push(`<circle class="cp-dot${s.own ? ' cp-dot-own' : ''}" cx="${p[0]}" cy="${p[1]}" r="10"/>`
            + `<text class="cp-dot-n${s.own ? ' cp-dot-n-own' : ''}" x="${p[0]}" y="${p[1]}">${i + 1}</text>`);
    });
    return `<svg class="cp-car" viewBox="0 0 200 300" role="img" aria-label="${esc(t('ui.diagram_label'))}">
        <rect class="cp-wheel" x="30" y="46" width="10" height="36" rx="3"/><rect class="cp-wheel" x="160" y="46" width="10" height="36" rx="3"/>
        <rect class="cp-wheel" x="30" y="214" width="10" height="36" rx="3"/><rect class="cp-wheel" x="160" y="214" width="10" height="36" rx="3"/>
        <path class="cp-body" d="M38 118 L27 114 Q23 118 27 124 L38 126 Z"/><path class="cp-body" d="M162 118 L173 114 Q177 118 173 124 L162 126 Z"/>
        <path class="cp-body" d="M64 12 C50 12 42 18 40 32 L37 96 C36 150 36 200 38 262 C40 282 52 290 68 290 L132 290 C148 290 160 282 162 262 C164 200 164 150 163 96 L160 32 C158 18 150 12 136 12 Z"/>
        <path class="cp-head" d="M50 19 Q58 15 72 16 L70 24 Q58 24 48 27 Z"/><path class="cp-head" d="M150 19 Q142 15 128 16 L130 24 Q142 24 152 27 Z"/>
        <rect class="cp-tail" x="48" y="281" width="20" height="5" rx="2"/><rect class="cp-tail" x="132" y="281" width="20" height="5" rx="2"/>
        <path class="cp-glass" d="M46 98 Q100 86 154 98 L148 124 Q100 116 52 124 Z"/>
        <rect class="cp-int" x="46" y="124" width="108" height="102" rx="6"/>
        <path class="cp-int" d="M52 124 Q100 116 148 124 L148 136 L52 136 Z"/>
        <circle class="cp-line" cx="76" cy="148" r="9"/><circle class="cp-line" cx="76" cy="148" r="2"/>
        <rect class="cp-int" x="60" y="156" width="30" height="32" rx="6"/><rect class="cp-int" x="110" y="156" width="30" height="32" rx="6"/>
        <rect class="cp-pillar" x="37" y="189" width="9" height="8"/><rect class="cp-pillar" x="154" y="189" width="9" height="8"/>
        <rect class="cp-int" x="56" y="200" width="88" height="24" rx="6"/>
        <path class="cp-glass" d="M50 228 Q100 236 150 228 L156 246 Q100 256 44 246 Z"/>
        <path class="cp-line" d="M37 126 L46 126 M154 126 L163 126 M37 240 L46 240 M154 240 L163 240"/>
        ${dots.join('')}
    </svg>`;
}

function readHtml(b) {
    const lab = b.label || {};
    const own = CFG.lang['read.' + b.id];
    const parts = [];
    if (own) parts.push(esc(own));
    else if (lab.field) parts.push(esc(t('read.generic_field', { field: lab.field })));
    // «printed on the label» only where the maker documents a label with it
    else if ((lab.locations || []).length && !lab.plateOnly) parts.push(esc(t('read.generic_nofield')));
    if (lab.examples && lab.examples.length) {
        parts.push(esc(t('read.examples')) + ' ' + lab.examples.map((e) => `<span class="cp-plate"><mark>${esc(e)}</mark></span>`).join(' '));
    }
    return parts.join('<br>');
}

/* The maker's own VIN lookup, named — the address as text to type, not a
   link (the page links nowhere). */
function vinHtml(b, vinText) {
    const v = b.vin || {};
    let s = '';
    if (v.route === 'official_form' && v.url) {
        s = esc(t('vinr.official_form', { brand: b.name })) + ` <span class="cp-addr">${esc(plainAddress(v.url))}</span>.`;
        if (v.phone) s += ' ' + esc(t('vinr.phone', { phone: v.phone }));
    } else if (v.route === 'official_phone') {
        s = esc(t('vinr.official_phone', { brand: b.name })) + (v.phone ? ' <span class="cp-addr">' + esc(v.phone) + '</span>' : '');
    } else if (v.route === 'dealer') {
        s = esc(t('vinr.dealer', { brand: b.name }));
    } else {
        s = esc(t('vinr.none', { brand: b.name }));
    }
    /* A maker's lookup serves ITS market (Toyota USA's form knows US-market
       VINs, Mazda Japan's phone line — Japanese cars): say whose cars. */
    if ((v.route === 'official_form' || v.route === 'official_phone') && v.market) {
        s += ' ' + esc(t('vinr.market_only', { market: t('market.' + v.market) }));
    }
    return `<div class="cp-note">${vinText ? esc(vinText) + ' ' : ''}${s}</div>`;
}

/* ── colour rows ──────────────────────────────────────────────────────────── */

function swatch(r) {
    const hex = r[F.rgb] || FAMILY_HEX[r[F.family]] || FAMILY_HEX.other;
    const approx = r[F.rgb] ? 0 : 1;
    return `<span class="cp-sw" data-finish="${esc(r[F.finish])}" data-approx="${approx}" style="background:${hex}" title="${esc(approx ? t('ui.swatch_family') : t('ui.swatch_maker'))}"></span>`;
}

function yearsTxt(y) {
    if (!y) return '';
    if (y[0] && y[1]) return y[0] === y[1] ? String(y[0]) : y[0] + '–' + y[1];
    if (y[0]) return y[0] + '–';
    return '–' + y[1];
}

/* The name a reader can read first: Russian on the Russian page, English
   elsewhere; the second line is the other known name (a Japanese or Korean
   original stays visible under it — that is what the maker printed). */
function rowNames(r) {
    const name = r[F.name], en = r[F.nameEn], ru = r[F.nameRu];
    const first = RU ? (ru || en || name) : (en || name);
    const second = RU ? (first === ru ? (en || name) : (first !== name ? name : null)) : (name !== first ? name : null);
    return [first, second && second !== first ? second : null].filter(Boolean);
}

/* Which market's colours a reader most likely owns — listed first, code
   order within. A Russian reader gets RU-market rows before Japan-only ones
   (a Mitsubishi list used to open on Minicab kei-truck colours). */
const MARKET_RANK = RU ? { ru: 0, owners: 0, eu: 1, us: 1, global: 1 }
    : CFG.locale === 'ko' ? { kr: 0, owners: 0, us: 1, eu: 1, global: 1 }
    : { eu: 0, us: 0, global: 0, owners: 0, ru: 1 };
function marketRank(r) {
    const ms = r[F.markets] && r[F.markets].length ? r[F.markets] : [r[F.market]];
    return Math.min(...ms.map((m) => (m in MARKET_RANK ? MARKET_RANK[m] : 2)));
}

function modelsTxt(b, r) {
    const ms = (r[F.models] || []).map((m) => modelLabel(b, 'm:' + m));
    return ms.slice(0, 6).join(', ') + (ms.length > 6 ? ' +' + (ms.length - 6) : '');
}

/* A row is a <button>: phrasing content only inside (spans, shown as blocks
   by CSS) — a <div> in a button is invalid and screen readers flatten it. */
function rowHtml(b, r, hit) {
    const names = rowNames(r);
    const meta = [t('finish.' + r[F.finish])];
    if (r[F.ptsName]) meta.push(t('ui.pts', { name: r[F.ptsName] }));
    if (r[F.models] && r[F.models].length) meta.push(modelsTxt(b, r));
    const y = yearsTxt(r[F.years]); if (y) meta.push(y);
    const markets = (r[F.markets] && r[F.markets].length ? r[F.markets] : [r[F.market]]).slice(0, 3);
    const tag = r[F.market] === 'owners'
        ? `<span class="cp-tag cp-tag-own">${esc(t('ui.tag_owners', { n: r.owners || 1 }))}</span>`
        : `<span class="cp-tag">${esc(markets.map((m) => t('market.' + m)).join(' · '))}</span>`;
    const alt = r[F.codeAlt] ? ` <span class="cp-alt">/ ${esc(r[F.codeAlt])}</span>` : '';
    return `<button type="button" class="cp-row${hit ? ' cp-row-hit' : ''}" aria-expanded="false" data-code="${esc(r[F.code])}" data-brand="${esc(b.id)}">
        ${swatch(r)}
        <span class="cp-rc"><span class="cp-line1"><span class="cp-code">${esc(r[F.code])}</span>${alt} <span class="cp-name">${esc(names[0] || '')}</span></span>
            ${names[1] ? `<span class="cp-meta">${esc(names[1])}</span>` : ''}
            <span class="cp-meta">${esc(meta.join(' · '))}</span></span>
        ${tag}
    </button>`;
}

function sourceHtml(b, r) {
    if (r[F.market] === 'owners') return `<div class="cp-meta cp-src">${esc(t('ui.source_owners'))}</div>`;
    const url = r[F.src] >= 0 ? b.sources[r[F.src]] : null;
    if (!url) return '';
    const s = sourceInfo(url);
    const where = s.fileHost ? t('ui.source_official', { brand: b.name })
        : s.year ? t('ui.source_dated', { site: s.site, year: s.year }) : s.site;
    return `<div class="cp-meta cp-src">${esc(t('ui.source'))}: ${esc(where)}</div>`;
}

/* What a shop or a colourist needs from this row, in one line. */
function shareLine(b, r) {
    return b.name + ' ' + r[F.code] + (rowNames(r)[0] ? ' — ' + rowNames(r)[0] : '');
}

const CAN_SHARE = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

function detailHtml(b, r) {
    const lines = [];
    if (r[F.finish] === 'metallic' || r[F.finish] === 'pearl' || r[F.finish] === 'mica') lines.push(t('ui.detail_effect'));
    if (r[F.finish] === 'tricoat') lines.push(t('ui.detail_tricoat'));
    lines.push(t('ui.detail_variants'));
    const alt = (r[F.altNames] || []).filter(Boolean);
    return `<div class="cp-detail">
        ${alt.length ? `<div class="cp-meta cp-also">${esc(t('ui.also_called'))} ${esc(alt.join(' · '))}</div>` : ''}
        <ul class="cp-dl">${lines.map((l) => `<li>${esc(l)}</li>`).join('')}</ul>
        <div class="cp-acts">
            <button type="button" class="cp-chip" data-copy="${esc(shareLine(b, r))}" data-copy-code="${esc(r[F.code])}">⧉ ${esc(t('ui.copy_code'))}</button>
            ${CAN_SHARE ? `<button type="button" class="cp-chip" data-share="${esc(r[F.code])}">↗ ${esc(t('ui.share'))}</button>` : ''}
            <button type="button" class="cp-chip" data-calc="${esc(r[F.finish])}">🧮 ${esc(t('ui.to_calc'))}</button>
            <button type="button" class="cp-chip" data-to-garage="${esc(r[F.code])}" data-tb="${esc(b.id)}">🚗 ${esc(t('ui.to_garage'))}</button>
        </div>
        <div class="cp-hint cp-garage-msg" role="status" hidden></div>
        ${sourceHtml(b, r)}
    </div>`;
}

function rowOf(b, code) { return b.colors.find((x) => x[F.code] === code); }

function toggleRow(row) {
    const b = IDX.byId.get(row.dataset.brand);
    if (!b) return;
    const open = row.getAttribute('aria-expanded') === 'true';
    const next = row.nextElementSibling;
    if (next && next.classList.contains('cp-detail')) next.remove();
    row.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (open) return;
    const r = rowOf(b, row.dataset.code);
    if (r) row.insertAdjacentHTML('afterend', detailHtml(b, r));
    ae('feature_use', { action: 'color_open', brand: b.id, code: row.dataset.code, model: CUR && CUR.model ? modelLabel(b, CUR.model) : '' });
    complete('color', { brand: b.id });
    remember({ b: b.id, m: CUR && CUR.brand === b.id ? CUR.model || '' : '', c: row.dataset.code });
}

/* One listener for everything drawn into the result area — the area is
   redrawn, the listener is not (a listener per redraw toggled a row open
   and shut again on the third make). */
function onOutClick(e) {
    const hit = (sel) => e.target.closest(sel);
    let x;
    if ((x = hit('[data-copy]'))) { copyCode(x.dataset.copy, x.closest('.cp-detail').previousElementSibling); return; }
    if ((x = hit('[data-share]'))) { shareCode(x.closest('.cp-detail').previousElementSibling); return; }
    if ((x = hit('[data-calc]'))) { openCalc(x.dataset.calc, x.closest('.cp-detail').previousElementSibling); return; }
    if ((x = hit('[data-to-garage]'))) { toGarage(x); return; }
    if ((x = hit('[data-near]'))) { openBrand(x.dataset.nb, { code: x.dataset.near, via: 'near', push: true }); return; }
    if ((x = hit('[data-open-model]'))) { openBrand(x.dataset.nb, { model: x.dataset.openModel, via: 'code_or_model', push: true }); return; }
    const card = hit('[data-brand-card]');
    const b = card && CUR && IDX.byId.get(CUR.brand);
    if (b && (x = hit('[data-fam]'))) {
        CUR.fam = x.dataset.fam;
        renderColors(card, b);
        ae('feature_use', { action: 'family_filter', brand: b.id, fam: CUR.fam, model: CUR.model ? modelLabel(b, CUR.model) : '' });
        return;
    }
    if (b && hit('[data-model-reset]')) {
        const ms = card.querySelector('[data-f="model"]');
        if (ms) ms.value = '';
        pickModel(card, b, '', 'reset');
        return;
    }
    const row = hit('.cp-row');
    if (!row) return;
    // a bare code's answer lists several makes: a row there opens its make
    if (!CUR || !CUR.brand) { openBrand(row.dataset.brand, { code: row.dataset.code, via: 'code', push: true }); return; }
    toggleRow(row);
}

async function copyCode(text, row) {
    const brand = row && row.dataset.brand || '', code = row && row.dataset.code || '';
    try { await navigator.clipboard.writeText(text); window.__toast && window.__toast(t('ui.copied', { code: text }), 'success'); }
    catch (e) { window.__toast && window.__toast(text, 'info'); }
    ae('feature_use', { action: 'copy_code', brand, code });
}

/* Send the code to the colourist, to oneself, to a chat: the phone's own
   share sheet. The link is this page with the make and code in the hash. */
async function shareCode(row) {
    const b = row && IDX.byId.get(row.dataset.brand);
    const r = b && rowOf(b, row.dataset.code);
    if (!r) return;
    let ok = 0;
    try {
        await navigator.share({ title: t('ui.brand_title', { brand: b.name }), text: shareLine(b, r), url: location.href });
        ok = 1;
    } catch (e) { /* the sheet was closed — not an error */ }
    ae('feature_use', { action: 'share', brand: b.id, code: r[F.code], ok });
}

/* ── the colour list of a make ────────────────────────────────────────────── */

function modelOptions(b, sel) {
    const mi = modelIndex(b);
    const opt = (v, label) => `<option value="${esc(v)}"${v === sel ? ' selected' : ''}>${esc(label)}</option>`;
    let h = opt('', t('ui.model_all', { n: b.colors.length }));
    for (const it of mi.items) {
        if (it.type === 'm') { h += opt('m:' + it.name, it.label + ' (' + it.n + ')'); continue; }
        h += `<optgroup label="${esc(it.label)}">` + opt('f:' + it.label, t('ui.model_family_all', { model: it.label, n: it.n }))
            + it.members.map((m) => opt('m:' + m.name, m.label + ' (' + m.n + ')')).join('') + '</optgroup>';
    }
    return h;
}

function brandCard(b, o) {
    const n = b.colors.length;
    /* Whose word each place is: the maker's (with its source) first; owners'
       reports only as such, with how many owners said so — numbered on from
       the maker's, so every number in the list has its dot. With neither,
       say so and ask: an owner at the car is the one who can fill the gap. */
    const official = b.label.locations || [];
    const owners = (b.ownerLabels || []).filter((x) => official.indexOf(x.where) < 0);
    const spots = official.map((w) => ({ where: w })).concat(owners.map((x) => ({ where: x.where, own: true })));
    const locList = official.map((l) => `<li>${esc(t('loc.' + l))}</li>`).join('');
    const labSrc = (b.label.sources || []).map((i) => b.sources[i]).filter(Boolean)[0];
    const labSrcTxt = labSrc ? (() => { const s = sourceInfo(labSrc); return s.fileHost ? t('ui.source_official', { brand: b.name }) : s.year ? t('ui.source_dated', { site: s.site, year: s.year }) : s.site; })() : '';
    const whereHtml = (locList ? `<ol class="cp-locs">${locList}</ol>`
            + (b.label.plateOnly ? `<div class="cp-hint">${esc(t('ui.plate_only', { brand: b.name }))}</div>` : '')
            + (labSrcTxt ? `<div class="cp-meta cp-src">${esc(t('ui.source'))}: ${esc(labSrcTxt)}</div>` : '')
        : '')
        + (owners.length ? `<div class="cp-sub cp-own-h">${esc(t('ui.where_owners'))}</div>
            <ol class="cp-locs cp-locs-own" start="${official.length + 1}">${owners.map((x) => `<li>${esc(t('loc.' + x.where))} <span class="cp-tag cp-tag-own">${esc(t('ui.tag_owners', { n: x.n }))}</span></li>`).join('')}</ol>` : '')
        + (!locList && !owners.length ? `<div class="cp-empty">${esc(t('ui.where_unknown'))}</div>` : '')
        + (!locList || b.label.plateOnly ? `<div class="cp-hint">${esc(t('ui.where_ask'))} <button type="button" class="cp-chip" data-ask-prefill="where">${esc(t('ui.where_ask_btn'))}</button></div>` : '');
    const mi = modelIndex(b);
    const colorsBlock = n
        ? `<div class="cp-filter">
                ${mi.items.length ? `<label class="cp-mlab"><span>${esc(t('ui.model_label'))}</span>
                    <select data-f="model">${modelOptions(b, o.model || '')}</select></label>` : ''}
                <label class="cp-mlab cp-qlab"><span>${esc(t('ui.color_label'))}</span>
                    <input type="search" data-f="q" placeholder="${esc(t('ui.filter_ph'))}" value="${esc(o.filter || '')}" enterkeyhint="search" autocomplete="off" spellcheck="false"></label>
           </div>
           <div class="cp-fams" data-fams></div>
           <div data-mnote></div>
           <div class="cp-count" data-count aria-live="polite"></div>
           <div class="cp-list" data-list></div>
           <div class="cp-hint">${esc(t('ui.screen_note'))}</div>`
        : `<div class="cp-empty cp-gap">${esc(t('ui.no_colors', { brand: b.name }))}</div>`;
    return `<div class="cp-card" data-brand-card="${esc(b.id)}">
        <h2 class="cp-card-h">${esc(t('ui.brand_title', { brand: brandLabel(b) }))}</h2>
        <div class="cp-sub">${n ? esc(t('ui.brand_count', { n })) : ''}</div>
        <div class="cp-where">
            <figure class="cp-fig">${carSvg(spots)}<figcaption class="cp-hint">${esc(t('ui.diagram_note'))}</figcaption></figure>
            <div>
                <h3 class="cp-where-h">${esc(t(b.label.plateOnly && locList ? 'ui.where_title_plate' : 'ui.where_title'))}</h3>
                ${whereHtml}
                <div class="cp-read">${readHtml(b)}</div>
            </div>
        </div>
        ${vinHtml(b, o.vinText)}
        ${colorsBlock}
    </div>`;
}

let filterNoneTimer = 0;

function renderColors(card, b) {
    const st = CUR;
    const list = card.querySelector('[data-list]');
    if (!list) return;
    const inM = st.model ? b.colors.filter((r) => inModel(b, r, st.model)) : b.colors;
    const fams = familiesOf(b, inM);
    if (st.fam && !fams.some((x) => x.f === st.fam)) st.fam = '';
    card.querySelector('[data-fams]').innerHTML =
        `<button type="button" class="cp-chip" data-fam="" aria-pressed="${st.fam ? 'false' : 'true'}">${esc(t('ui.all'))} <span class="cp-n">${inM.length}</span></button>`
        + fams.map((x) => `<button type="button" class="cp-chip" data-fam="${x.f}" aria-pressed="${st.fam === x.f ? 'true' : 'false'}"><span class="cp-fam" style="background:${FAMILY_HEX[x.f]}"></span>${esc(t('family.' + x.f))} <span class="cp-n">${x.n}</span></button>`).join('');

    const mi = modelIndex(b);
    const note = card.querySelector('[data-mnote]');
    if (st.model) {
        const extra = mi.unmodelled ? ' ' + t('ui.model_unmodelled', { n: mi.unmodelled }) : '';
        note.innerHTML = `<div class="cp-note">${esc(t('ui.model_note', { brand: b.name, model: modelLabel(b, st.model) }) + extra)}
            <button type="button" class="cp-chip" data-model-reset>${esc(t('ui.model_show_all'))}</button></div>`;
    } else if (st.modelAsked) {
        note.innerHTML = `<div class="cp-note cp-warn">${esc(t('ui.model_unknown', { brand: b.name, model: st.modelAsked }))}</div>`;
    } else note.innerHTML = '';

    const rows = filterColors(b, st.q, st.fam, st.model);
    const hk = st.code ? codeKey(st.code) : null;
    // the hit first, then the reader's market, then catalogue (code) order
    rows.sort((a, c) => (hk ? (codeKey(c[F.code]) === hk) - (codeKey(a[F.code]) === hk) : 0) || marketRank(a) - marketRank(c));
    const LIMIT = 80;
    list.innerHTML = rows.length
        ? rows.slice(0, LIMIT).map((r) => rowHtml(b, r, hk && codeKey(r[F.code]) === hk)).join('')
          + (rows.length > LIMIT ? `<div class="cp-hint">${esc(t('ui.more_rows', { n: rows.length - LIMIT }))}</div>` : '')
        : `<div class="cp-empty">${esc(t('ui.filter_none'))}</div>`;
    const filtered = rows.length !== b.colors.length;
    card.querySelector('[data-count]').textContent = filtered ? t('ui.shown', { n: rows.length, total: b.colors.length }) : '';
    /* What people look for and do not find is the list of what to add next. */
    clearTimeout(filterNoneTimer);
    if (!rows.length && st.q) filterNoneTimer = setTimeout(() => ae('feature_use', { action: 'filter_none', brand: b.id, q: String(st.q).slice(0, 24), model: st.model ? modelLabel(b, st.model) : '' }), 1200);
}

function openBrand(id, opts) {
    const o = opts || {};
    const b = IDX.byId.get(id);
    if (!b) return;
    /* A model the catalogue knows becomes the picker's value; one it does not
       know (an old LADA Priora) is said out loud, and every colour shows. */
    const sel = o.model ? resolveModel(b, o.model) : null;
    CUR = { brand: id, code: o.code || null, model: sel, modelAsked: o.model && !sel ? String(o.model).replace(/^[fm]:/, '') : '', q: o.filter || '', fam: '' };
    markPicker(id);
    const out = $('cpOut');
    out.innerHTML = (o.code ? codeAnswerHtml(o.code, id) : '') + brandCard(b, { model: sel, filter: o.filter, vinText: o.vinText });
    const card = out.querySelector('[data-brand-card]');
    renderColors(card, b);
    wireAsk(out, { brand: id, model: sel ? modelLabel(b, sel) : (o.model || null), code: o.code || null });
    // the inputs are new on every draw, so are their listeners
    const qi = card.querySelector('[data-f="q"]');
    if (qi) {
        qi.addEventListener('input', () => { CUR.q = qi.value; renderColors(card, b); });
        // a phone's «search» key: the list is already filtered, the keyboard only covers it
        qi.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); qi.blur(); } });
    }
    const ms = card.querySelector('[data-f="model"]');
    if (ms) ms.addEventListener('change', () => pickModel(card, b, ms.value, 'select'));
    setHash({ b: id, c: o.code || '', m: sel || '' }, o.push);
    if (sel) remember({ b: id, m: sel, c: o.code || '' });
    ae('feature_use', { action: 'brand_open', brand: id, via: o.via || '', n: b.colors.length, model: sel ? modelLabel(b, sel) : '' });
    if (o.model && !sel) ae('feature_use', { action: 'model_unknown', brand: id, model: String(o.model).slice(0, 40) });
    if (sel) ae('feature_use', { action: 'model_pick', brand: id, model: modelLabel(b, sel), via: o.via || '', n: filterColors(b, '', '', sel).length });
    stage('brand', { brand: id });
    try { window.__announce && window.__announce(t('ui.brand_title', { brand: b.name })); } catch (e) {}
}

function pickModel(card, b, sel, via) {
    CUR.model = sel || null;
    CUR.modelAsked = '';
    renderColors(card, b);
    setHash({ b: b.id, c: CUR.code || '', m: CUR.model || '' }, false);
    if (sel) remember({ b: b.id, m: sel, c: '' });
    ae('feature_use', { action: 'model_pick', brand: b.id, model: sel ? modelLabel(b, sel) : '', via, n: filterColors(b, '', '', sel).length });
}

/* The answer to «what is code X?» — shown above the make's card when the
   make is known, alone (possibly across makes) when it is not. */
function codeAnswerHtml(code, brand, models) {
    const res = lookupCode(code, IDX, brand || undefined);
    const shown = fold(code);
    /* A bare word that is also a model («A6»): offer the model too. */
    const alsoModel = (models || []).length
        ? `<div class="cp-note">${esc(t('ui.code_or_model'))} ${models.map((h) => {
            const bb = IDX.byId.get(h.brand);
            return `<button type="button" class="cp-chip" data-open-model="${esc(h.sel)}" data-nb="${esc(h.brand)}">${esc(bb.name + ' ' + h.label)}</button>`;
        }).join(' ')}</div>` : '';
    if (res.exact.length) {
        const byBrand = groupHits(res.exact);
        ae('feature_use', { action: 'code_found', brand: brand || '', code: shown.slice(0, 12), n: res.exact.length, brands: byBrand.length });
        complete('code', { brand: brand || '' });
        return `<div class="cp-card cp-hit" data-answer>
            <h2 class="cp-card-h">${esc(t(brand ? 'ui.code_is' : 'ui.code_is_multi', { code: shown }))}</h2>
            <div class="cp-list">${byBrand.map(([bid, rows]) => {
                const bb = IDX.byId.get(bid);
                return (brand ? '' : `<div class="cp-sub cp-brand-h"><b>${esc(brandLabel(bb))}</b></div>`) + rows.map((r) => rowHtml(bb, r, true)).join('');
            }).join('')}</div>
            ${!brand && byBrand.length > 1 ? `<div class="cp-note">${esc(t('ui.code_many_makes'))}</div>` : ''}
            ${alsoModel}
        </div>`;
    }
    ae('feature_use', { action: 'code_missing', brand: brand || '', code: shown.slice(0, 12), near: res.near.length });
    const near = res.near.length
        ? `<div class="cp-note cp-warn">${esc(t('ui.code_near'))} ${res.near.slice(0, 6).map((h) => `<button type="button" class="cp-chip" data-near="${esc(h.row[F.code])}" data-nb="${esc(h.brand)}">${esc(h.row[F.code])} · ${esc(IDX.byId.get(h.brand).name)}</button>`).join(' ')}</div>`
        : '';
    return `<div class="cp-card" data-answer>
        <h2 class="cp-card-h">${esc(t('ui.code_unknown', { code: shown }))}</h2>
        <div class="cp-sub">${esc(t(brand ? 'ui.code_unknown_brand' : 'ui.code_unknown_hint'))}</div>
        ${near}
        ${alsoModel}
        <div class="cp-photo-row"><button type="button" class="lg-btn" data-ask-prefill="1">➕ ${esc(t('ui.add_this'))}</button></div>
    </div>`;
}

function groupHits(hits) {
    const m = new Map();
    for (const h of hits) { if (!m.has(h.brand)) m.set(h.brand, []); m.get(h.brand).push(h.row); }
    return Array.from(m.entries());
}

function showCode(code, opts) {
    const o = opts || {};
    CUR = { brand: null, model: null, code, q: '', fam: '' };
    markPicker(null);
    const out = $('cpOut');
    /* A row here stands for its own make (a code can be several makes'):
       each row carries its make, never «the first make that has this code». */
    out.innerHTML = codeAnswerHtml(code, null, o.models);
    wireAsk(out, { code });
    setHash({ c: code }, o.push);
    stage('code');
}

function showVin(vin) {
    const d = decodeVin(vin);
    const out = $('cpOut');
    if (!d.valid) {
        out.innerHTML = `<div class="cp-card"><h2 class="cp-card-h">${esc(t('ui.vin_bad'))}</h2>
            <div class="cp-sub">${esc(t('ui.vin_bad_hint'))}</div></div>`;
        ae('feature_use', { action: 'vin', valid: 0 });
        return;
    }
    const bid = brandFromManufacturer(d.manufacturer);
    const b = bid && IDX.byId.get(bid);
    const yr = d.year && d.year.years ? d.year.years.join(' / ') : '';
    const vinText = t('ui.vin_decoded', { maker: String(d.manufacturer || '?').split('|')[0], year: yr || '—' });
    /* The WMI (maker + plant) is what tells us which makes people check;
       the rest of a VIN identifies one car and never leaves the page. */
    ae('feature_use', { action: 'vin', valid: 1, wmi: vin.slice(0, 3), brand: bid || '', maker_known: d.manufacturer ? 1 : 0 });
    stage('vin');
    if (b) {
        openBrand(b.id, { via: 'vin', vinText: vinText + ' ' + t('ui.vin_no_color'), push: true });
        return;
    }
    out.innerHTML = `<div class="cp-card"><h2 class="cp-card-h">${esc(vinText)}</h2>
        <div class="cp-sub">${esc(t('ui.vin_no_color'))} ${esc(t('ui.vin_pick_brand'))}</div></div>`;
}

/* ── search box ───────────────────────────────────────────────────────────── */

function runQuery(raw, via) {
    if (!IDX) return;
    const c = classify(raw, IDX);
    /* The words themselves only when the page did not understand them — the
       list of what to teach it. A VIN or a code is not needed here. */
    ae('feature_use', { action: 'search', kind: c.kind, via: via || 'input', q: c.kind === 'unknown' ? String(raw).trim().slice(0, 40) : '' });
    if (c.kind === 'empty') { $('cpOut').innerHTML = ''; setHash({}, true); return; }
    if (c.kind === 'vin') { showVin(c.vin); return; }
    if (c.kind === 'brand') {
        openBrand(c.brand, { model: c.modelSel || c.model, code: c.code, via: 'search', push: true });
        return;
    }
    if (c.kind === 'code') { showCode(c.code, { models: c.models, push: true }); return; }
    $('cpOut').innerHTML = `<div class="cp-card"><h2 class="cp-card-h">${esc(t('ui.not_understood'))}</h2>
        <div class="cp-sub">${esc(t('ui.not_understood_hint'))}</div></div>`;
    try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'query_not_understood', len: String(raw).trim().length }); } catch (e) {}
}

/* ── label photo ──────────────────────────────────────────────────────────── */

/* The reader's models (12–20 MB) start loading the moment the person reaches
   for a photo — tapping the zone or the camera button — not after the photo
   arrives. Measured 01–27.10: the engine was ready 8–12 s after the photo on a
   desktop, 20 s on an 8 GB Android, 48 s on a 4 GB one (wasm); picking the
   photo itself takes 3–17 s, which this hides. Save-Data is respected. */
let ocrOpening = null;
function openOcr(via) {
    if (ocrEngine) return Promise.resolve(ocrEngine);
    if (ocrOpening) return ocrOpening;
    if (!window.PPOCR) return null;
    if (via === 'intent') {
        try { if (navigator.connection && navigator.connection.saveData) return null; } catch (e) {}
    }
    const lang = CYR_OCR ? 'ru' : 'en';
    const tl = performance.now();
    const bar = $('cpShotBar');
    ocrOpening = (async () => {
        const cached = await window.PPOCR.isCached('en', CYR_OCR ? 'ru' : undefined).catch(() => false);
        const eng = await window.PPOCR.open('en', {
            alsoLang: CYR_OCR ? 'ru' : undefined,
            workerUrl: CFG.workerUrl || undefined,
            onProgress: (f) => { if (bar) bar.firstElementChild.style.width = Math.round(f * 100) + '%'; },
        });
        ocrEngine = eng;
        ae('feature_use', { action: 'ocr_ready', provider: eng.provider || '', cached: cached ? 1 : 0, lang, ms: Math.round(performance.now() - tl), via });
        return eng;
    })();
    // a failed load is retried by the next photo, not remembered
    ocrOpening.catch(() => { ocrOpening = null; });
    return ocrOpening;
}

/* Reading a 12 MP photo on a phone takes a minute and more (134 s measured on
   a 4 GB Android): the status says how long it has been going, so a person
   does not take the silence for a hang. Seconds in the page's own language. */
function statusClock(el, t0) {
    let base = el.textContent;
    let fmt = null;
    try { fmt = new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : undefined, { style: 'unit', unit: 'second', unitDisplay: 'short' }); } catch (e) {}
    const draw = () => {
        const sec = Math.round((performance.now() - t0) / 1000);
        el.textContent = sec >= 5 && fmt ? base + ' · ' + fmt.format(sec) : base;
    };
    const id = setInterval(draw, 1000);
    return {
        set(text) { base = text; draw(); },
        stop() { clearInterval(id); },
    };
}

/* A newer photo supersedes the one being read: 27.10 the same label was picked
   twice while the first read ran, and the two reads shared one phone for 84 s.
   The older run stops at its next pass and never touches the page again. */
let photoGen = 0;
const SUPERSEDED = { superseded: true };

async function onPhoto(file) {
    if (!file) return;
    const gen = ++photoGen;
    const alive = () => gen === photoGen;
    const card = $('cpShotCard');
    card.hidden = false;
    const status = $('cpShotStatus'), bar = $('cpShotBar'), out = $('cpShotOut');
    out.innerHTML = '';
    status.textContent = t('ui.shot_opening');
    stage('photo');
    ae('feature_use', { action: 'photo_start', size: file.size, type: file.type || '', warm: ocrEngine ? 2 : (ocrOpening ? 1 : 0) });
    // the photo is here: the models load now if the tap did not start them
    openOcr('photo');
    let bitmap;
    try {
        /* An iPhone photo picked from the gallery is HEIC, which Chrome and
           Edge cannot decode natively; the shared decoder turns it into JPEG. */
        let src = file;
        if (window.HeicDecode && window.HeicDecode.isHeic(file)) src = await window.HeicDecode.toBlob(file, 'image/jpeg', 0.92, window.__ae || null);
        /* Safari до 17, Chrome и Firefox до 111 значения 'from-image' не знают и
           бросают TypeError — снимок объявлялся нечитаемым. Их умолчание и было
           «как в EXIF» (замер на Chromium 110 / Firefox 108 / WebKit 16.4),
           поэтому повтор без опции ничего не теряет. */
        try { bitmap = await createImageBitmap(src, { imageOrientation: 'from-image' }); }
        catch (e0) { bitmap = await createImageBitmap(src); }
    } catch (e) {
        if (!alive()) return;
        status.textContent = t('ui.shot_unreadable');
        ae('tool_error', { action: 'photo_unreadable', type: file.type || '', msg: String(e && e.message || e).slice(0, 120) });
        return;
    }
    if (!alive()) { bitmap.close(); return; }
    const canvas = $('cpShot');
    const scale = Math.min(1, 1400 / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    if (!window.PPOCR) { status.textContent = t('ui.ocr_unavailable'); ae('tool_error', { action: 'ocr_missing' }); return; }
    const t0 = performance.now();
    const clock = statusClock(status, t0);
    try {
        if (!ocrEngine) {
            const cached = await window.PPOCR.isCached('en', CYR_OCR ? 'ru' : undefined).catch(() => false);
            if (!cached) {
                const mb = window.PPOCR.downloadMb('en', false, CYR_OCR ? 'ru' : undefined);
                clock.set(t('ui.ocr_download', { mb: Math.round(mb) }));
            } else clock.set(t('ui.ocr_starting'));
            bar.hidden = false;
            await openOcr('photo');
            // what the PERSON waited for the engine, after the photo was in
            ae('feature_use', { action: 'ocr_wait', ms: Math.round(performance.now() - t0) });
        }
        if (!alive()) throw SUPERSEDED;
        clock.set(t('ui.ocr_reading'));
        bar.hidden = true;
        const { lines, r, pass } = await readPhoto(bitmap, clock, alive);
        clock.stop();
        const hit = r.candidates[0] && r.candidates[0].hits.length ? 1 : 0;
        ae('feature_use', { action: 'ocr_done', ms: Math.round(performance.now() - t0), lines: lines.length, pass,
            cands: r.candidates.length, brand: r.brand || '', via: r.brandVia || '', hit, w: bitmap.width, h: bitmap.height });
        if (!r.candidates.length) {
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'photo_no_code', lines: lines.length, pass, brand: r.brand || '' }); } catch (e) {}
        }
        drawBoxes(ctx, lines, r, bitmap.width / canvas.width);
        bitmap.close();
        out.dataset.pass = pass;
        out.dataset.ms = String(Math.round(performance.now() - t0));
        if (hit) complete('photo', { brand: r.brand || '', pass });
        renderShotResult(r, lines);
    } catch (e) {
        clock.stop();
        if (e === SUPERSEDED || !alive()) {
            try { bitmap.close(); } catch (e2) {}
            ae('feature_use', { action: 'ocr_superseded', ms: Math.round(performance.now() - t0) });
            return;
        }
        bar.hidden = true;
        status.textContent = t('ui.ocr_failed');
        ae('tool_error', { action: 'ocr_failed', msg: String(e && e.message || e).slice(0, 120) });
    }
}

/* A label is a small thing in a big photo. The detector sees the whole frame
   at 960 px, so a label a metre away — text 20 px high on a 12 MP photo —
   shrinks to 5 px and is not found at all (bench: 0 of 4 «small» shots read
   with one pass). So, when the first pass finds no code the catalogue knows:
     zoom  — crop around the text the detector DID find, from the original
             photo, and read that crop;
     tiles — found no text at all: read four overlapping quarters.
   Each pass gets its own bitmap: read() transfers it to the worker, and the
   original must survive for the next crop. */
async function readPhoto(bitmap, clock, alive) {
    const W = bitmap.width, H = bitmap.height;
    const pass = async (sx, sy, sw, sh, limit) => {
        if (alive && !alive()) throw SUPERSEDED;
        const f = Math.min(1, 2400 / Math.max(sw, sh));
        const bm = await createImageBitmap(bitmap, Math.round(sx), Math.round(sy), Math.round(sw), Math.round(sh),
            { resizeWidth: Math.max(1, Math.round(sw * f)), resizeHeight: Math.max(1, Math.round(sh * f)), resizeQuality: 'high' });
        const res = await ocrEngine.read(bm, { cfg: { detLimit: limit } });
        return (res.lines || []).map((l) => ({
            text: l.alt && l.alt.text && /[А-Яа-я]/.test(l.alt.text) ? l.alt.text : l.text,
            box: l.box ? { x: sx + l.box.x / f, y: sy + l.box.y / f, w: l.box.w / f, h: l.box.h / f } : null,
            conf: l.conf,
        }));
    };
    const judge = (lines) => readLabel(lines, IDX, { decodeVin, brand: CUR && CUR.brand });
    /* «Found» means the catalogue knows the code AND something on the label
       points at it — a caption, the maker's rule, its fixed place. A bare word
       that happens to be a code (BY from «MFD. BY», a Ford code) is a guess:
       the zoom and tile passes still run and may read the real caption. */
    const strong = (r) => r.candidates.length && r.candidates[0].hits.length && r.candidates[0].why !== 'token';
    const better = (a, b) => (strong(a) && !strong(b)) || (!!strong(a) === !!strong(b) && (a.candidates[0] ? a.candidates[0].score : -1) > (b.candidates[0] ? b.candidates[0].score : -1));

    /* A phone photo is 4000 px across; at the default 960 the detector sees a
       label a metre away as 4 px text and finds only its bold heading. Scale
       the detector with the photo (up to 1600) — cheaper than tiles. */
    let lines = await pass(0, 0, W, H, Math.min(1600, Math.max(960, Math.round(Math.max(W, H) / 2.5))));
    let r = judge(lines), used = 'a';
    if (strong(r)) return { lines, r, pass: used };

    const boxed = lines.filter((l) => l.box && l.box.w > 0);
    if (boxed.length) {
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (const l of boxed) { x0 = Math.min(x0, l.box.x); y0 = Math.min(y0, l.box.y); x1 = Math.max(x1, l.box.x + l.box.w); y1 = Math.max(y1, l.box.y + l.box.h); }
        // what was found is often just the heading: the label runs on below it
        const pw = (x1 - x0) * 0.2 + 24, ph = Math.max((y1 - y0) * 0.5, (x1 - x0) * 0.35) + 24;
        x0 = Math.max(0, x0 - pw); y0 = Math.max(0, y0 - ph); x1 = Math.min(W, x1 + pw); y1 = Math.min(H, y1 + ph);
        if ((x1 - x0) * (y1 - y0) < 0.6 * W * H) {
            clock.set(t('ui.ocr_zoom'));
            const zl = await pass(x0, y0, x1 - x0, y1 - y0, 1600);
            const zr = judge(zl);
            if (better(zr, r)) { lines = zl; r = zr; used = 'zoom'; }
            if (strong(r)) return { lines, r, pass: used };
        }
    }

    clock.set(t('ui.ocr_zoom'));
    const tw = W * 0.6, th = H * 0.6, merged = [];
    for (const [fx, fy] of [[0, 0], [0.4, 0], [0, 0.4], [0.4, 0.4]]) {
        for (const l of await pass(fx * W, fy * H, tw, th, 1280)) {
            // tiles overlap by a fifth: the same line read twice is one line
            const dup = merged.find((m) => m.text === l.text && m.box && l.box && Math.abs(m.box.y - l.box.y) < l.box.h / 2 && Math.abs(m.box.x - l.box.x) < l.box.h * 2);
            if (!dup) merged.push(l);
        }
    }
    merged.sort((a, b) => (a.box && b.box ? (Math.abs(a.box.y - b.box.y) < Math.min(a.box.h, b.box.h) / 2 ? a.box.x - b.box.x : a.box.y - b.box.y) : 0));
    const tr = judge(merged);
    if (better(tr, r)) { lines = merged; r = tr; used = 'tiles'; }
    return { lines, r, pass: used };
}

function drawBoxes(ctx, lines, r, k) {
    const best = r.candidates[0];
    for (const l of lines) {
        if (!l.box) continue;
        const isBest = best && best.line && best.line.text === l.text;
        ctx.lineWidth = isBest ? 4 : 1.5;
        ctx.strokeStyle = isBest ? '#facc15' : 'rgba(96,165,250,.7)';
        ctx.strokeRect(l.box.x / k, l.box.y / k, l.box.w / k, l.box.h / k);
    }
}

function renderShotResult(r, lines) {
    const status = $('cpShotStatus'), out = $('cpShotOut');
    const b = r.brand ? IDX.byId.get(r.brand) : null;
    out.dataset.brand = r.brand || '';
    out.dataset.via = r.brandVia || '';
    const via = r.brandVia === 'vin' ? t('ui.brand_via_vin') : r.brandVia === 'label' ? t('ui.brand_via_label') : r.brandVia === 'chosen' ? t('ui.brand_via_chosen') : '';
    if (!r.candidates.length) {
        status.textContent = t('ui.shot_none');
    } else {
        status.textContent = b ? t('ui.shot_found_brand', { brand: b.name, via }) : t('ui.shot_found');
    }
    const chips = r.candidates.map((c, i) => `<button type="button" class="cp-chip cp-cand" data-cand="${esc(c.code)}" aria-pressed="${i === 0 ? 'true' : 'false'}">${esc(c.code)}${c.hits.length ? ' ✓' : ''}</button>`).join('');
    out.innerHTML = `${chips ? `<div class="cp-sub">${esc(t('ui.shot_pick'))}</div><div class="cp-cands">${chips}</div>` : ''}
        <div class="cp-lines"><details><summary>${esc(t('ui.shot_lines', { n: lines.length }))}</summary><pre>${esc(lines.map((l) => l.text).join('\n'))}</pre></details></div>`;
    out.querySelectorAll('[data-cand]').forEach((btn) => btn.addEventListener('click', () => {
        out.querySelectorAll('[data-cand]').forEach((x) => x.setAttribute('aria-pressed', x === btn ? 'true' : 'false'));
        pickCandidate(btn.dataset.cand, r, 'pick');
    }));
    if (r.candidates.length) pickCandidate(r.candidates[0].code, r, 'auto');
}

function pickCandidate(code, r, how) {
    ae('feature_use', { action: 'ocr_pick', how, brand: r.brand || '', code });
    if (r.brand) {
        openBrand(r.brand, { code, via: 'photo', push: how === 'auto' });
        wireAsk($('cpOut'), { code, brand: r.brand });
    } else showCode(code, { push: how === 'auto' });
}

/* ── owners' submissions ──────────────────────────────────────────────────── */

/* Every «add» button in the result wires once; what is known about the car
   (make, model, code) is merged in as later calls learn it. A button that
   asks WHERE the label was focuses that field rather than the code. */
function wireAsk(root, pre) {
    for (const btn of root.querySelectorAll('[data-ask-prefill]')) {
        btn.__askPre = Object.assign({}, btn.__askPre || {}, pre);
        if (btn.__askWired) continue;
        btn.__askWired = true;
        btn.addEventListener('click', () => {
            const p = btn.__askPre || {};
            const b = p.brand && IDX.byId.get(p.brand);
            if (b) $('cpAskBrand').value = b.name;
            if (p.model) $('cpAskModel').value = p.model;
            if (p.code) $('cpAskCode').value = askCode(p.code, p.brand);
            $('cpAskForm').hidden = false; $('cpAskDone').hidden = true;
            const focus = btn.dataset.askPrefill === 'where' ? $('cpAskWhere') : $('cpAskCode');
            focus.focus({ preventScroll: true });
            $('cpAsk').scrollIntoView({ block: 'nearest' });
            ae('feature_use', { action: 'ask_prefill', brand: p.brand || '', what: btn.dataset.askPrefill || 'code' });
        });
    }
}

/* Codes are printed in Latin capitals and a Russian keyboard types the
   look-alikes (А2В for A2B) — fold them. Except for a make whose own codes ARE
   Cyrillic (UAZ): folding would turn its code into one that does not exist. */
function askCode(code, brandId) {
    const b = brandId && IDX.byId.get(brandId);
    const cyr = b && b.colors.some((r) => /[А-ЯЁ]/i.test(r[F.code]));
    return cyr ? String(code).toUpperCase() : fold(code);
}

/* «Reviewed» publishes an owner's code, and 27.10 a Kia Rio owner sent
   «3683584» — seven digits, where every Kia code is two or three characters
   (UD, PGU, SWP): a number off another sticker. The form says so BEFORE it
   sends; the owner may still send as is (the row is marked, and the server
   does not publish a code outside the make's pattern either —
   CarPaintCommunityController::fitsMakeFormat). A make without a pattern or
   examples is not judged: there is nothing to show the person. */
function askFormatWarning(code, brandId) {
    const b = brandId && IDX.byId.get(brandId);
    if (!b || !b.label || !(b.label.examples || []).length) return '';
    if (fitsBrand(code, b) !== false) return '';
    if (lookupCode(code, IDX, brandId).exact.length) return ''; // a published code, typed its own way
    return t('ui.ask_code_format', { brand: b.name, examples: b.label.examples.slice(0, 4).join(', '), code: String(code).slice(0, 12) });
}

let askWarnedFor = '';   // the code the warning was shown for: sending it again means «as is»

function resetAskWarning() {
    askWarnedFor = '';
    const w = $('cpAskWarn');
    if (w) { w.hidden = true; w.textContent = ''; }
    const b = $('cpAskSend');
    if (b.dataset.label) b.textContent = b.dataset.label;
}

async function sendAsk() {
    const v = (id) => String($(id).value || '').trim();
    const code = v('cpAskCode'), brandTxt = v('cpAskBrand');
    if (!code || !brandTxt) { window.__toast && window.__toast(t('ui.ask_need'), 'error'); return; }
    const bb = classify(brandTxt, IDX);
    const brandId = bb.kind === 'brand' ? bb.brand : '';
    const warn = askFormatWarning(code, brandId);
    const warnEl = $('cpAskWarn'), sendBtn = $('cpAskSend');
    const asIs = !!warn && askWarnedFor === brandId + '|' + code;
    if (warn && !asIs) {
        askWarnedFor = brandId + '|' + code;
        warnEl.textContent = warn;
        warnEl.hidden = false;
        if (!sendBtn.dataset.label) sendBtn.dataset.label = sendBtn.textContent;
        sendBtn.textContent = t('ui.ask_send_anyway');
        $('cpAskCode').focus({ preventScroll: true });
        warnEl.scrollIntoView({ block: 'nearest' });
        ae('feature_use', { action: 'ask_format_warn', brand: brandId, len: code.length });
        return;
    }
    const payload = {
        tool_slug: SLUG,
        locale: CFG.locale,
        data: {
            brand: brandTxt, brand_id: bb.kind === 'brand' ? bb.brand : '', model: v('cpAskModel'), year: v('cpAskYear'),
            /* «color_name», not «name»: ToolSuggestion::fileDeduped collapses a
               submitter's pending rows by data.name, and one owner sending two
               cars' codes that are both «Белый» must file two rows. */
            code: askCode(code, bb.kind === 'brand' ? bb.brand : ''), color_name: v('cpAskName'), where: $('cpAskWhere').value || '', note: v('cpAskNote'),
            known: lookupCode(code, IDX, bb.kind === 'brand' ? bb.brand : undefined).exact.length ? '1' : '0',
        },
    };
    // sent after the warning: the moderator sees it, the public list skips it
    if (asIs) payload.data.code_format = 'mismatch';
    const send = (tk) => fetch('/api/tool-suggestion', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': tk, 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: JSON.stringify(payload),
    });
    const btn = $('cpAskSend');
    btn.disabled = true;
    try {
        let r = await send((document.querySelector('meta[name="csrf-token"]') || {}).content || '');
        if (r.status === 419 && window.__refreshCsrf) r = await send(await window.__refreshCsrf());
        btn.disabled = false;
        if (r.ok) {
            $('cpAskForm').hidden = true; $('cpAskDone').hidden = false;
            for (const id of ['cpAskCode', 'cpAskName', 'cpAskNote', 'cpAskYear']) $(id).value = '';
            $('cpAskWhere').value = '';
            resetAskWarning();
            ae('feature_use', { action: 'suggest_submitted', brand: payload.data.brand_id || 'other', known: payload.data.known, where: payload.data.where ? 1 : 0, fmt: asIs ? 'mismatch' : (warn ? '' : 'ok') });
        } else {
            window.__toast && window.__toast(t(r.status === 429 ? 'ui.ask_rate' : 'ui.ask_failed'), 'error');
            ae('tool_error', { action: 'suggest_failed', status: r.status });
        }
    } catch (e) {
        btn.disabled = false;
        window.__toast && window.__toast(t('ui.ask_failed'), 'error');
        ae('tool_error', { action: 'suggest_failed', status: 0 });
    }
}

/* ── paint calculators ────────────────────────────────────────────────────── */

/* «How much to buy» for the colour just opened: its finish decides the
   system (a metallic needs a base and a clear coat), the rest is the
   calculator's own form below. */
function openCalc(finish, row) {
    const el = $('cpCalc');
    if (!el) return;
    presetCalc(finish);
    el.scrollIntoView({ block: 'nearest' });
    const first = el.querySelector('select, input, button');
    if (first) first.focus({ preventScroll: true });
    ae('feature_use', { action: 'calc_from_color', brand: row && row.dataset.brand || '', finish });
}

/* ── the garage (./garage-link.js, loaded on demand) ─────────────────────── */

let garageP = null;
const garage = () => (garageP = garageP || import('./garage-link.js').then((m) => m.garageCars().then((g) => ({ m, g }))).catch(() => null));

/* «Это мой цвет — сохранить в гараж»: the car of this make in the account's garage or the browser's. */
async function toGarage(btn) {
    const b = IDX.byId.get(btn.dataset.tb);
    const r = b && rowOf(b, btn.dataset.toGarage);
    const msg = btn.closest('.cp-detail').querySelector('.cp-garage-msg');
    if (!r) return;
    btn.disabled = true;
    try {
        const x = await garage();
        const car = x ? x.m.carOfBrand(x.g, b.id, CFG.makePaint) : null;
        if (!car) {
            msg.hidden = false;
            msg.innerHTML = esc(t('ui.to_garage_none', { make: b.name })) + (CFG.garageAddUrl ? ` <a href="${esc(CFG.garageAddUrl)}">${esc(t('ui.to_garage_add'))}</a>` : '');
            ae('feature_use', { action: 'to_garage', ok: 0, why: 'no_car', brand: b.id });
            return;
        }
        const ok = await x.m.saveToGarage(x.g, car, {
            code: r[F.code], name: rowNames(r)[0] || '', family: r[F.family] || '', hex: r[F.rgb] || '',
            src: r[F.market] === 'owners' ? 'owners' : 'maker',
        });
        if (!ok) throw new Error('not saved');
        car.code = r[F.code];
        msg.hidden = false;
        msg.textContent = '✓ ' + t('ui.to_garage_done', { car: x.m.carText(car) });
        ae('feature_use', { action: 'to_garage', ok: 1, src: x.g.source, brand: b.id });
    } catch (e) {
        msg.hidden = false;
        msg.textContent = t('ui.to_garage_failed');
        ae('tool_error', { action: 'to_garage_failed', status: e && e.status ? e.status : 0 });
    } finally {
        btn.disabled = false;
    }
}

/* Nothing asked yet (no link, no ?q=): the page opens on the garage car's make and model,
   its own code when it has one — the same car every car tool starts from. */
async function fromGarage() {
    const x = await garage();
    if (!x || CUR || location.hash) return;
    const map = CFG.makePaint || {};
    const car = (x.g.cars || []).find((c) => map[c.makeId] && IDX.byId.has(map[c.makeId]));
    if (!car) return;
    const b = IDX.byId.get(map[car.makeId]);
    const strip = $('cpGarageCar');
    if (strip) { strip.hidden = false; strip.textContent = t('ui.garage_car', { car: x.m.carText(car) + (car.code ? ' · ' + car.code : '') }); }
    openBrand(b.id, { model: car.model || null, code: car.code && rowOf(b, car.code) ? car.code : null, via: 'garage' });
}

/* ── the address bar ──────────────────────────────────────────────────────── */

/* A look-up is a place the back button returns to (a phone's back gesture
   used to leave the tool from the third make); narrowing it — a model, a
   filter — replaces the entry instead of piling them up. */
let shownHash = null;
function setHash(p, push) {
    const parts = [];
    for (const k of ['b', 'c', 'm']) if (p[k]) parts.push(k + '=' + encodeURIComponent(p[k]));
    const h = parts.length ? '#' + parts.join('&') : '';
    if (h === location.hash || (!h && !location.hash)) return;
    const url = location.pathname + location.search + h;
    if (push) history.pushState({ cp: 1 }, '', url); else history.replaceState(history.state, '', url);
    shownHash = location.hash;
}

function fromHash(via) {
    const h = new URLSearchParams(location.hash.replace(/^#/, ''));
    const b = h.get('b'), c = h.get('c'), m = h.get('m');
    if (b && IDX.byId.has(b)) { openBrand(b, { code: c || null, model: m || null, via }); return true; }
    if (c) { showCode(c); return true; }
    return false;
}

/* Back and forward fire popstate AND hashchange for one step: draw once. */
function onNavigate() {
    if (!IDX || location.hash === shownHash) return;
    shownHash = location.hash;
    if (!fromHash('back')) { $('cpOut').innerHTML = ''; markPicker(null); CUR = null; }
}

/* ── mount ────────────────────────────────────────────────────────────────── */

function mount() {
    const q = $('cpQ');
    $('cpGo').addEventListener('click', () => runQuery(q.value, 'button'));
    q.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); runQuery(q.value, 'enter'); } });
    $('cpSamples').addEventListener('click', (e) => {
        const s = e.target.closest('[data-sample]');
        if (!s) return;
        q.value = s.dataset.sample;
        runQuery(s.dataset.sample, 'sample');
    });
    $('cpRecent').addEventListener('click', (e) => {
        const r = e.target.closest('[data-recent]');
        if (!r) return;
        const x = recentList()[+r.dataset.recent];
        if (!x) return;
        openBrand(x.b, { model: x.m || null, code: x.c || null, via: 'recent', push: true });
    });
    $('cpCamBtn').addEventListener('click', () => { openOcr('intent'); $('cp-cam-input').click(); });
    $('cp-cam-input').addEventListener('change', (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; onPhoto(f); });
    const drop = $('cpDrop');
    /* This module loads AFTER the page: a label photographed the moment the
       page appeared would reach nobody through a plain listener. The shared
       helper also replays a file picked before it attached. */
    if (drop) {
        // reaching for a photo (tap, click, keyboard, a file dragged over) starts the models
        for (const ev of ['pointerdown', 'click', 'dragenter']) drop.addEventListener(ev, () => openOcr('intent'), { passive: true });
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(drop, (f) => onPhoto(f));
        else drop.addEventListener('tool-uploader:file', (e) => onPhoto(e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]))));
    }
    $('cpAskSend').addEventListener('click', sendAsk);
    for (const id of ['cpAskCode', 'cpAskBrand']) $(id).addEventListener('input', () => { if (askWarnedFor) resetAskWarning(); });
    mountCalc($('cpCalcApp'), { t, locale: CFG.locale, ae });
    $('cpAskAgain').addEventListener('click', () => { $('cpAskForm').hidden = false; $('cpAskDone').hidden = true; $('cpAskCode').focus({ preventScroll: true }); });
    $('cpOut').addEventListener('click', onOutClick);
    window.addEventListener('popstate', onNavigate);
    window.addEventListener('hashchange', onNavigate);

    loadCatalog().then(() => {
        renderBrandPicker();
        renderRecent();
        stage('ready');
        shownHash = location.hash;
        if (!fromHash('link')) {
            const urlQ = new URLSearchParams(location.search).get('q');
            if (urlQ) { q.value = urlQ; runQuery(urlQ, 'url'); }
            else fromGarage();
        }
    }).catch((e) => {
        $('cpBrandGrid').textContent = t('ui.load_failed');
        ae('tool_error', { action: 'catalog_load_failed', msg: String(e && e.message || e).slice(0, 120) });
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
else mount();
